import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Plus, Check } from "lucide-react";
import { useCasting } from "@/context/CastingContext";
import { useLanguage } from "@/context/LanguageContext";

export const TalentCard = ({ talent, index = 0 }) => {
  const { has, toggle } = useCasting();
  const { t, tCat } = useLanguage();
  const active = has(talent.id);

  return (
    <motion.article
      data-testid={`talent-card-${talent.id}`}
      data-cursor
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="group relative"
    >
      <Link to={`/talent/${talent.id}`} className="block" aria-label={talent.name}>
        <div className="relative aspect-[3/4] overflow-hidden border border-white/10 bg-[#121212]">
          <img
            src={talent.image_url}
            alt={`${talent.name} — ${talent.division === "VOICE" ? "voice talent" : talent.division === "ACTORS" ? "actor" : "model"} portrait`}
            loading="lazy"
            className="h-full w-full object-cover object-top grayscale-[40%] transition-[transform,filter] duration-700 ease-out group-hover:scale-[1.05] group-hover:grayscale-0"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
          <button
            data-testid={`talent-favorite-toggle-${talent.id}`}
            aria-label={`${t("profile.addShortlist")} — ${talent.name}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggle(talent);
            }}
            className={`absolute right-3 top-3 flex h-9 w-9 items-center justify-center border backdrop-blur-md transition-colors duration-300 ${
              active
                ? "border-gold bg-gold text-black"
                : "border-white/25 bg-black/40 text-white hover:border-gold hover:text-gold"
            }`}
          >
            {active ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          </button>
          {talent.new_face && (
            <span className="absolute left-3 top-3 border border-gold/50 bg-black/50 px-2 py-1 font-mono text-[8px] uppercase tracking-[0.25em] text-gold backdrop-blur-md">
              {t("profile.newFace")}
            </span>
          )}
          <div className="absolute inset-x-0 bottom-0 p-4">
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-gold">
              {(talent.categories || []).slice(0, 2).map(tCat).join(" · ")}
            </div>
            <h3 className="mt-1 font-syne text-lg font-bold uppercase leading-tight tracking-wide text-white">
              {talent.name}
            </h3>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-white/60">
              {talent.height ? `H ${talent.height.split("/")[1]?.trim() || talent.height} — ` : ""}
              {talent.location}
            </div>
          </div>
        </div>
      </Link>
    </motion.article>
  );
};
